import React from 'react';
import { Box, InputAdornment, TextField, type SxProps, type Theme } from '@mui/material';
import { SmartToy as RobotIcon } from '@mui/icons-material';
import { useAssistant } from '../AssistantContext';

interface AssistantAskBarProps {
  /** Lets callers (e.g. Header, which renders this centered in a 48px AppBar) override sizing. */
  sx?: SxProps<Theme>;
}

/**
 * The always-present ask box, rendered centered in the Header. Typing a question and pressing
 * Enter (or clicking away is not enough — Enter submits) opens the shared assistant modal seeded
 * with that message, then the conversation continues there. Because it drives the same
 * AssistantContext as the Header robot icon and the modal, the box and the modal are one
 * continuous thread.
 *
 * Rendered only where `Assistant.Guidance` is client-visible (Header gates it), so it never
 * appears in an environment where the endpoint would 404.
 */
const AssistantAskBar: React.FC<AssistantAskBarProps> = ({ sx }) => {
  const { openAssistant, sendMessage, loading } = useAssistant();
  const [input, setInput] = React.useState('');

  const submit = () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    openAssistant();
    void sendMessage(text);
  };

  return (
    <Box sx={sx}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search or type a question"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        inputProps={{ maxLength: 4000, 'aria-label': 'Ask the AskIAM Assistant' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <RobotIcon color="primary" fontSize="small" />
            </InputAdornment>
          ),
          // The header this sits in is a flat colour literal, not a theme
          // surface token, so 'background.paper' (which flips dark/light with
          // the mode) keeps a light, readable field in both - matching the
          // reference screenshot's light pill look regardless of mode.
          sx: { bgcolor: 'background.paper', borderRadius: 1 },
        }}
      />
    </Box>
  );
};

export default AssistantAskBar;
